const convertNodeToMarkdown = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const tag = node.tagName.toLowerCase();
    const children = () => Array.from(node.childNodes).map(convertNodeToMarkdown).join('');

    switch (tag) {
        case 'h1': return `# ${children().trim()}\n\n`;
        case 'h2': return `## ${children().trim()}\n\n`;
        case 'h3': return `### ${children().trim()}\n\n`;
        case 'h4': return `#### ${children().trim()}\n\n`;
        case 'h5': return `##### ${children().trim()}\n\n`;
        case 'h6': return `###### ${children().trim()}\n\n`;
        case 'p': {
            const text = children();
            return text.trim() ? `${text}\n\n` : '\n';
        }
        case 'strong':
        case 'b': return `**${children()}**`;
        case 'em':
        case 'i': return `*${children()}*`;
        case 's':
        case 'del': return `~~${children()}~~`;
        case 'u': return children();
        case 'a': return `[${children()}](${node.getAttribute('href') || ''})`;
        case 'img': return `![${node.getAttribute('alt') || ''}](${node.getAttribute('src') || ''})`;
        case 'code':
            if (node.parentNode?.tagName?.toLowerCase() === 'pre') return children();
            return `\`${children()}\``;
        case 'pre': {
            const codeEl = node.querySelector('code');
            const lang = codeEl?.className?.match(/language-(\w+)/)?.[1] || '';
            return `\`\`\`${lang}\n${node.textContent}\n\`\`\`\n\n`;
        }
        case 'blockquote': {
            const text = children().trim();
            return text.split('\n').map(l => `> ${l}`).join('\n') + '\n\n';
        }
        case 'ul': {
            const items = Array.from(node.children)
                .filter(c => c.tagName.toLowerCase() === 'li')
                .map(li => `- ${li.textContent.trim()}`);
            return items.join('\n') + '\n\n';
        }
        case 'ol': {
            const items = Array.from(node.children)
                .filter(c => c.tagName.toLowerCase() === 'li')
                .map((li, i) => `${i + 1}. ${li.textContent.trim()}`);
            return items.join('\n') + '\n\n';
        }
        case 'li': return '';
        case 'br': return '\n';
        case 'hr': return '---\n\n';
        case 'table': return tableToMarkdown(node);
        default: return children();
    }
};

const tableToMarkdown = (table) => {
    const rows = Array.from(table.querySelectorAll('tr'));
    if (!rows.length) return '';

    const data = rows.map(row =>
        Array.from(row.querySelectorAll('th, td')).map(cell =>
            cell.textContent.trim().replace(/\|/g, '\\|')
        )
    );

    const cols = Math.max(...data.map(r => r.length));
    const widths = Array(cols).fill(3);
    data.forEach(row => row.forEach((cell, i) => { widths[i] = Math.max(widths[i], cell.length); }));

    const pad = (cell, i) => cell.padEnd(widths[i]);
    const rowToMd = row => '| ' + Array(cols).fill('').map((_, i) => pad(row[i] || '', i)).join(' | ') + ' |';
    const sep = '| ' + widths.map(w => '-'.repeat(w)).join(' | ') + ' |';

    return [rowToMd(data[0]), sep, ...data.slice(1).map(rowToMd)].join('\n') + '\n\n';
};

const htmlToMarkdown = (html) => {
    if (!html) return '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return Array.from(doc.body.childNodes).map(convertNodeToMarkdown).join('').trim();
};

export const triggerDownload = (content, filename, mimeType) => {
    const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
};

const sanitizeFilename = (name) => (name || 'document').replace(/[^a-z0-9\-_. ]/gi, '_').trim();

export const downloadAsMarkdown = (content, title, metadata = {}) => {
    const lines = [`# ${title}\n`];
    if (metadata.createdAt) lines.push(`**Created:** ${metadata.createdAt}  `);
    if (metadata.updatedAt) lines.push(`**Updated:** ${metadata.updatedAt}  `);
    if (metadata.views !== undefined) lines.push(`**Views:** ${metadata.views}\n`);
    lines.push('---\n');
    lines.push(htmlToMarkdown(content));

    triggerDownload(lines.join('\n'), `${sanitizeFilename(title)}.md`, 'text/markdown;charset=utf-8');
};

export const downloadAsJSON = (data, title) => {
    triggerDownload(JSON.stringify(data, null, 2), `${sanitizeFilename(title)}.json`, 'application/json;charset=utf-8');
};

const buildDocMarkdown = (title, data) => {
    const lines = [`# ${title}\n`];
    const createdAt = data.created_at ? new Date(data.created_at).toLocaleString() : '';
    const updatedAt = data.updated_at ? new Date(data.updated_at).toLocaleString() : '';
    if (createdAt) lines.push(`**Created:** ${createdAt}  `);
    if (updatedAt) lines.push(`**Updated:** ${updatedAt}  `);
    if (data.views !== undefined) lines.push(`**Views:** ${data.views}\n`);
    lines.push('---\n');
    lines.push(htmlToMarkdown(data.content || ''));
    return lines.join('\n');
};

export const downloadBulkAsZip = async (docs, format, zipName) => {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    const usedFilenames = new Set();

    for (const { title, data } of docs) {
        let base = sanitizeFilename(title);
        let filename = base;
        let counter = 1;
        while (usedFilenames.has(filename)) {
            filename = `${base}_${counter++}`;
        }
        usedFilenames.add(filename);

        if (format === 'markdown') {
            zip.file(`${filename}.md`, buildDocMarkdown(title, data));
        } else {
            zip.file(`${filename}.json`, JSON.stringify(data, null, 2));
        }
    }

    const blob = await zip.generateAsync({ type: 'blob' });
    triggerDownload(blob, `${sanitizeFilename(zipName)}.zip`);
};

// ── PDF export (BBD-115 / BBD-119) ──────────────────────────────────────────
// PDFs are rendered on the server (real text, links, page numbers, a table of
// contents), so unlike Markdown/JSON these helpers ask the backend for the
// file instead of building it here. Document.js, WorkspaceDocument.js,
// SharedDocumentView.js and the export download page all go through them.

const API_BASE = 'https://bluebirddocumentationadmin.pythonanywhere.com';
const PDF_OPTIONS_STORAGE_KEY = 'bb-pdf-export-options';

export const PDF_PAGE_SIZES = ['A4', 'Letter'];

// Letter is the everyday paper size in the US and Canada; everywhere else is A4.
export const defaultPdfPageSize = () => {
    const language = (typeof navigator !== 'undefined' && navigator.language) || '';
    return /^en-(US|CA)$/i.test(language) ? 'Letter' : 'A4';
};

// includeToc stays null until the user picks: the server then adds a table of
// contents whenever the PDF holds more than one document.
export const loadPdfOptions = () => {
    const options = { pageSize: defaultPdfPageSize(), includeToc: null, includeMetadata: true };
    try {
        const saved = JSON.parse(window.localStorage.getItem(PDF_OPTIONS_STORAGE_KEY) || 'null');
        if (saved && typeof saved === 'object') {
            if (PDF_PAGE_SIZES.includes(saved.pageSize)) options.pageSize = saved.pageSize;
            if (typeof saved.includeToc === 'boolean') options.includeToc = saved.includeToc;
            if (typeof saved.includeMetadata === 'boolean') options.includeMetadata = saved.includeMetadata;
        }
    } catch (err) {
        // Private browsing or blocked storage: fall back to the defaults.
    }
    return options;
};

export const savePdfOptions = (options) => {
    try {
        window.localStorage.setItem(PDF_OPTIONS_STORAGE_KEY, JSON.stringify(options));
    } catch (err) {
        // Remembering the choice is a convenience; never fail an export over it.
    }
};

const toServerOptions = (options = {}) => ({
    page_size: options.pageSize || defaultPdfPageSize(),
    include_metadata: options.includeMetadata !== false,
    ...(typeof options.includeToc === 'boolean' ? { include_toc: options.includeToc } : {}),
});

const authHeaders = (token) => ({
    'Authorization': `${token}`,
    'auth-token': `Bearer ${token}`,
});

const PDF_ERROR_MESSAGES = {
    403: "You don't have permission to export this document.",
    404: 'This document could not be found.',
    413: 'This document is too large to export as a PDF.',
    429: 'You have exported a lot of PDFs in the last hour. Please try again later.',
    503: 'PDF export is temporarily unavailable. You can use Print and choose "Save as PDF" instead.',
};

// Resolves to { status: 'downloaded' } once the file has been handed to the
// browser, or { status: 'queued', jobId, message } when the export is large
// enough to be generated in the background. Rejects with a readable message.
const fetchPdf = async (url, init, title) => {
    let response;
    try {
        response = await fetch(url, init);
    } catch (err) {
        throw new Error('Could not reach the server. Check your connection and try again.');
    }

    if (response.status === 202) {
        const data = await response.json();
        return { status: 'queued', jobId: data.job_id, message: data.message };
    }
    if (!response.ok) {
        let serverMessage = '';
        try {
            serverMessage = (await response.json()).error || '';
        } catch (err) {
            // Not JSON (a proxy error page, for example).
        }
        throw new Error(PDF_ERROR_MESSAGES[response.status] || serverMessage || 'The PDF could not be generated. Please try again.');
    }

    const blob = await response.blob();
    triggerDownload(blob, `${sanitizeFilename(title)}.pdf`, 'application/pdf');
    return { status: 'downloaded' };
};

export const exportPersonalPdf = ({ token, type, name, author, includeChildren, options }) =>
    fetchPdf(`${API_BASE}/documentation/export/pdf/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body: JSON.stringify({
            type, name, author,
            include_children: Boolean(includeChildren),
            options: toServerOptions(options),
        }),
    }, name);

export const exportWorkspacePdf = ({ token, workspaceId, documentType, documentId, title, includeChildren, options }) =>
    fetchPdf(`${API_BASE}/workspaces/${workspaceId}/export/pdf/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body: JSON.stringify({
            document_type: documentType,
            document_id: documentId,
            include_children: Boolean(includeChildren),
            options: toServerOptions(options),
        }),
    }, title);

// A public, published document, for a reader who is not signed in. Always the
// single document; the server caches the file per document version.
export const exportPublicPdf = ({ type, name, author, options }) => {
    const query = new URLSearchParams(toServerOptions(options)).toString();
    const path = [type, name, author].map(encodeURIComponent).join('/');
    return fetchPdf(`${API_BASE}/documentation/export/pdf/public/${path}/?${query}`, { method: 'GET' }, name);
};

// Share links need no account: the token in the URL is the authorization.
// ps_… is a personal share, ws_… a workspace external share.
export const exportSharedPdf = ({ shareToken, title, options }) => {
    const query = new URLSearchParams(toServerOptions(options)).toString();
    const path = shareToken.startsWith('ws_')
        ? `/workspaces/shared/${encodeURIComponent(shareToken)}/pdf/`
        : `/documentation/export/pdf/shared/${encodeURIComponent(shareToken)}/`;
    return fetchPdf(`${API_BASE}${path}?${query}`, { method: 'GET' }, title);
};

// Background exports: the status of a job, and the finished file.
export const fetchPdfExportJob = async (token, jobId) => {
    const response = await fetch(`${API_BASE}/documentation/export/pdf/jobs/${jobId}/`, { headers: authHeaders(token) });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Could not load this export. Please try again.');
    return response.json();
};

export const downloadPdfExportJob = async (token, job) => {
    const response = await fetch(`${API_BASE}/documentation/export/pdf/jobs/${job.job_id}/download/`, { headers: authHeaders(token) });
    if (response.status === 410) throw new Error('This export has expired. Please export the document again.');
    if (!response.ok) throw new Error('The PDF could not be downloaded. Please try again.');
    const blob = await response.blob();
    triggerDownload(blob, job.filename || `${sanitizeFilename(job.title)}.pdf`, 'application/pdf');
};
